import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Edit2, Ticket, Copy, Megaphone, UserRound, Download } from "lucide-react";
import { adminClient } from "./api";

const EMPTY = { code: "", kind: "percent", value: "", min_subtotal: "", max_uses: "", starts_at: "", ends_at: "", is_active: true, blurb: "", show_on_site: false, customer_email: "", applies_to: "merch" };
const SCOPES = [
  { id: "merch", label: "Merch only" },
  { id: "services", label: "Services only" },
  { id: "both", label: "Both" },
];
const SCOPE_LABEL = { merch: "Merch", services: "Services", both: "Merch + Services" };
const KINDS = [
  { id: "percent", label: "% off" },
  { id: "fixed", label: "$ off" },
  { id: "free_shipping", label: "Free shipping" },
];
const STATE_STYLE = {
  live: "bg-emerald-100 text-emerald-800",
  scheduled: "bg-sky-100 text-sky-800",
  expired: "bg-zinc-200 text-zinc-600",
  used_up: "bg-amber-100 text-amber-800",
  paused: "bg-zinc-100 text-zinc-500",
};

const randomCode = () => "HARVEY" + Math.floor(10 + Math.random() * 90);

export default function PromoCodesAdmin() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ orders: 0, revenue: 0, discount_given: 0 });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => adminClient.get("/admin/promo-codes").then(({ data }) => { setItems(data.items || []); setTotals(data.totals || totals); }).catch(() => toast.error("Could not load promo codes"));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    try {
      const { data } = await adminClient.get("/admin/promo-codes/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `promo-redemptions-${new Date().toISOString().slice(0, 10)}.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const start = (p) => {
    setEditing(p || null);
    setForm(p ? { code: p.code, kind: p.kind, value: p.value ?? "", min_subtotal: p.min_subtotal || "", max_uses: p.max_uses ?? "", starts_at: p.starts_at || "", ends_at: p.ends_at || "", is_active: p.is_active, blurb: p.blurb || "", show_on_site: !!p.show_on_site, customer_email: p.customer_email || "", applies_to: p.applies_to || "merch" } : { ...EMPTY, code: randomCode() });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code.toUpperCase(),
        value: form.kind === "free_shipping" ? 0 : parseFloat(form.value || 0),
        min_subtotal: parseFloat(form.min_subtotal || 0),
        max_uses: form.max_uses === "" ? null : parseInt(form.max_uses, 10),
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        customer_email: form.customer_email.trim() || null,
      };
      if (editing) await adminClient.put(`/admin/promo-codes/${editing.id}`, payload);
      else await adminClient.post("/admin/promo-codes", payload);
      toast.success(editing ? "Promo updated" : "Promo created");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete code ${p.code}?`)) return;
    await adminClient.delete(`/admin/promo-codes/${p.id}`);
    toast.success("Promo deleted");
    load();
  };

  const togglePause = async (p) => {
    try {
      await adminClient.put(`/admin/promo-codes/${p.id}`, { ...p, is_active: !p.is_active });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success(`${code} copied`); };

  return (
    <div className="space-y-4" data-testid="promo-admin">
      <Card className="p-5 border-2 border-zinc-200">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-black text-lg text-[#0C2E2B] flex items-center gap-2"><Ticket className="w-5 h-5" /> Promo codes</h3>
            <p className="text-xs text-zinc-500">Customers type a code at merch checkout or in the booking form after their quote. Set % off, $ off, or free shipping — with optional minimum spend, use limit, dates and scope (merch / services / both).</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="promo-export-btn" onClick={exportCsv} className="rounded-md font-bold"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
            <Button data-testid="promo-new-btn" onClick={() => start(null)} className="rounded-md font-bold"><Plus className="w-4 h-4 mr-1" /> New code</Button>
          </div>
        </div>
        {items.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="promo-totals">
            {[["Orders + jobs with a code", `${totals.orders + (totals.bookings || 0)}`, "promo-total-orders"], ["Paid revenue", `$${Number(totals.revenue).toFixed(2)}`, "promo-total-revenue"], ["Discounts given", `$${Number(totals.discount_given).toFixed(2)}`, "promo-total-discount"]].map(([l, v, id]) => (
              <div key={id} className="rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">{l}</div>
                <div data-testid={id} className="font-mono-data font-black text-[#0C2E2B] text-lg">{v}</div>
              </div>
            ))}
          </div>
        )}
        {items.length === 0 ? <p className="text-sm text-zinc-500" data-testid="promo-empty">No promo codes yet.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((p) => (
              <div key={p.id} data-testid={`promo-row-${p.code}`} className={`rounded-md border-2 p-3 ${p.state === "live" ? "border-zinc-200" : "border-dashed border-zinc-300"}`}>
                <div className="flex items-center gap-2">
                  <button data-testid={`promo-copy-${p.code}`} onClick={() => copy(p.code)} className="font-mono-data font-black text-[#0C2E2B] text-lg tracking-wider flex items-center gap-1 hover:underline">{p.code} <Copy className="w-3 h-3 text-zinc-400" /></button>
                  {p.show_on_site && <span data-testid={`promo-featured-${p.code}`} className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#FFDF00] text-[#0C2E2B] flex items-center gap-1"><Megaphone className="w-3 h-3" /> On merch page</span>}
                  {p.customer_email && <span data-testid={`promo-personal-${p.code}`} className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 flex items-center gap-1"><UserRound className="w-3 h-3" /> Personal</span>}
                  <span data-testid={`promo-state-${p.code}`} className={`ml-auto text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${STATE_STYLE[p.state] || ""}`}>{p.state.replace("_", " ")}</span>
                </div>
                <div className="text-sm font-bold text-red-600 mt-1">{p.label}{p.min_subtotal > 0 && <span className="text-zinc-500 font-normal text-xs"> · min ${Number(p.min_subtotal).toFixed(2)}</span>} <span data-testid={`promo-scope-${p.code}`} className="ml-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 border border-zinc-300 rounded px-1.5 py-0.5">{SCOPE_LABEL[p.applies_to] || "Merch"}</span></div>
                {p.blurb && <div className="text-xs text-zinc-600">{p.blurb}</div>}
                {p.customer_email && <div className="text-xs text-sky-800">Only {p.customer_email} can redeem</div>}
                <div className="text-[11px] text-zinc-500 mt-1" data-testid={`promo-uses-${p.code}`}>
                  Used {p.uses}{p.max_uses ? ` / ${p.max_uses}` : " times"}
                  {(p.starts_at || p.ends_at) && <> · {p.starts_at || "now"} → {p.ends_at || "no end"}</>}
                </div>
                <div className="text-[11px] mt-1 flex flex-wrap gap-x-3" data-testid={`promo-stats-${p.code}`}>
                  <span className="text-zinc-700"><b className="font-mono-data">{p.stats.orders}</b> order{p.stats.orders === 1 ? "" : "s"}{p.applies_to !== "merch" && <> · <b className="font-mono-data">{p.stats.bookings || 0}</b> job{p.stats.bookings === 1 ? "" : "s"}</>}</span>
                  <span className="text-emerald-700"><b className="font-mono-data">${Number(p.stats.revenue).toFixed(2)}</b> paid revenue{p.stats.paid_orders !== (p.stats.orders + (p.stats.bookings || 0)) ? ` (${p.stats.paid_orders} paid)` : ""}</span>
                  <span className="text-red-600"><b className="font-mono-data">${Number(p.stats.discount_given).toFixed(2)}</b> given away</span>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" data-testid={`promo-edit-${p.code}`} onClick={() => start(p)} className="h-8 text-xs font-bold"><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" data-testid={`promo-toggle-${p.code}`} onClick={() => togglePause(p)} className="h-8 text-xs font-bold">{p.is_active ? "Pause" : "Resume"}</Button>
                  <Button size="sm" variant="outline" data-testid={`promo-delete-${p.code}`} onClick={() => remove(p)} className="h-8 text-xs font-bold text-red-600"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="promo-form">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-[#0C2E2B]">{editing ? "Edit promo code" : "New promo code"}</DialogTitle>
            <DialogDescription className="text-xs">Codes are case-insensitive; letters, numbers and dashes only.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Code</Label>
              <Input data-testid="promo-code-input" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "") })} placeholder="HARVEY10" className="mt-1 font-mono-data font-bold tracking-wider" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Discount type</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {KINDS.map((k) => (
                  <button type="button" key={k.id} data-testid={`promo-kind-${k.id}`} onClick={() => setForm({ ...form, kind: k.id })}
                    className={`p-2 rounded-md border-2 text-sm font-bold ${form.kind === k.id ? "border-[#0C2E2B] bg-[#FFDF00]/25 text-[#0C2E2B]" : "border-zinc-200 text-zinc-600"}`}>{k.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Applies to</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {SCOPES.map((s) => {
                  const locked = form.kind === "free_shipping" && s.id !== "merch";
                  const active = (form.kind === "free_shipping" ? "merch" : form.applies_to) === s.id;
                  return (
                    <button type="button" key={s.id} data-testid={`promo-scope-${s.id}`} disabled={locked} onClick={() => setForm({ ...form, applies_to: s.id })}
                      className={`p-2 rounded-md border-2 text-sm font-bold disabled:opacity-40 ${active ? "border-[#0C2E2B] bg-[#FFDF00]/25 text-[#0C2E2B]" : "border-zinc-200 text-zinc-600"}`}>{s.label}</button>
                  );
                })}
              </div>
              {form.kind === "free_shipping" ? <p className="text-[10px] text-zinc-500 mt-1">Free shipping only applies to merch orders.</p>
                : form.applies_to !== "merch" && <p className="text-[10px] text-zinc-500 mt-1">On service jobs the discount comes off the final job price, never the $25 deposit.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {form.kind !== "free_shipping" && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">{form.kind === "percent" ? "Percent off" : "Dollars off"}</Label>
                  <Input data-testid="promo-value-input" type="number" step={form.kind === "percent" ? "1" : "0.01"} min="0" max={form.kind === "percent" ? 100 : undefined} required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1" />
                </div>
              )}
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Minimum subtotal ($)</Label>
                <Input data-testid="promo-min-input" type="number" step="0.01" min="0" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} placeholder="0" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Max uses</Label>
                <Input data-testid="promo-max-uses-input" type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Unlimited" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Blurb (optional)</Label>
                <Input data-testid="promo-blurb-input" value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })} placeholder="Summer sale" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Starts</Label>
                <Input data-testid="promo-starts-input" type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Ends</Label>
                <Input data-testid="promo-ends-input" type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-700">Personal to one email (optional)</Label>
              <Input data-testid="promo-email-input" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder="customer@example.com — leave blank for everyone" className="mt-1" />
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="promo-show-on-site-switch" checked={form.show_on_site} onCheckedChange={(v) => setForm({ ...form, show_on_site: v })} disabled={!!form.customer_email.trim()} /> Advertise on the Merch page{form.customer_email.trim() ? <span className="text-xs text-zinc-400">(not for personal codes)</span> : null}</label>
            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 text-sm"><Switch data-testid="promo-active-switch" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /> {form.is_active ? "Active" : "Paused"}</label>
              <Button type="submit" data-testid="promo-save-btn" disabled={saving} className="rounded-md font-bold">{saving ? "Saving…" : editing ? "Save code" : "Create code"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
